import {
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  DynamicTableColDef,
  FrkBreakpoint,
  ResponsiveService,
} from '@frk/eds-components';
import { Product, ShareClass } from '@models';
import {
  LiteratureDocument,
  ShareClassCode,
  ShareclassLevelLiteratureDocumentListingData,
} from '@types';
import { Observable, Subject, Subscribable, Subscription } from 'rxjs';
import { EMDASH } from '@products/utils/constants/product.constants';
import cloneDeep from 'lodash/cloneDeep';
import { WINDOW } from '@ng-web-apis/common';
import { TranslateService } from '@shared/translate/translate.service';
import { COLDEF } from '../../interactive-content/ucits-etf-data-table/types/model/indexes';
import { takeUntil } from 'rxjs/operators';
import { GlobalRegulatoryTableData } from '@literature/types/global-regulatory.type';

@Component({
  selector: 'ft-literature-global-regulatory-listing',
  templateUrl: './literature-global-regulatory-listing.component.html',
  styleUrls: ['./literature-global-regulatory-listing.component.scss'],
})
export class LiteratureGlobalRegulatoryListingComponent
  implements OnInit, OnChanges, OnDestroy {
  isHandheld$: Observable<boolean>;
  unSubscribe$: Subject<void> = new Subject<void>();

  @Input() stateData: ShareclassLevelLiteratureDocumentListingData[] = [];
  @Input() colDefs: DynamicTableColDef<COLDEF>[] = [];

  public rowData: GlobalRegulatoryTableData[] = [];
  public FrkBreakpoint = FrkBreakpoint;
  EMDASH = EMDASH; // we can use it in template
  private isMobile: boolean;
  constructor(
    private responsiveService: ResponsiveService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    @Inject(WINDOW) readonly windowRef: Window
  ) {}
  ngOnInit(): void {
    this.isHandheld$ = this.responsiveService.isHandheld$();
    this.responsiveService
      .isMobile$()
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((mobile) => (this.isMobile = true));
  }

  ngOnChanges() {
    if (this.stateData.length) {
      this.prepareTableData(this.stateData);
    }
  }

  private prepareTableData(
    stateData: ShareclassLevelLiteratureDocumentListingData[]
  ): void {
    this.rowData = [];
    stateData.forEach((row, index) => {
      // To handle edge case when primaryshare class data is not present
      const isPrimaryShareClassDataAvailable =
        row?.shareclassOption.filter(
          (el) => el.id === row.primaryShareclassCode
        ).length === 1;

      if (!row.literatureData.hasOwnProperty(row.primaryShareclassCode)) {
        row.literatureData[row.primaryShareclassCode] = {};
      }

      this.rowData.push({
        flyoutMetaData: {
          items: row?.shareclassOption,
          flyoutScroll: true,
          isNonLinkableClickAction: true,
          widthSize: this.isMobile ? 'xl' : 'sm',
          flyoutSelectedMenuItem: this.onMenuItemSelected.bind(this, index),
        },
        selectedShareclass: isPrimaryShareClassDataAvailable
          ? row?.shareclassOption.filter(
              (el) => el.id === row.primaryShareclassCode
            )[0]?.link
          : row?.shareclassOption[0]?.link,
        fundName: row?.product?.fundName,
        docs: isPrimaryShareClassDataAvailable
          ? row.literatureData[row.primaryShareclassCode]
          : row.literatureData[row?.shareclassOption[0]?.id],
        viewMoreHref: this.getProductDetailLink(
          row?.product,
          isPrimaryShareClassDataAvailable
            ? row?.primaryShareclassCode
            : (row?.shareclassOption[0]?.id as ShareClassCode)
        ),
        product: row?.product,
        index,
      });
    });
  }

  private getProductDetailLink(
    product: Product,
    shareclassCode: ShareClassCode
  ): string {
    const shareclassData: ShareClass[] = product.shareClasses.filter(
      (data) => data.shareClassCode === shareclassCode
    );
    return shareclassData[0]?.link + '#documents';
  }

  public openDocument(literatureData: LiteratureDocument): void {
    const documentLink = literatureData?.pdfDownloadLink;
    this.windowRef.open(documentLink, '_blank');
  }

  public onMenuItemSelected(rowIndex: number, selectedShareClass: any): void {
    this.updateSpecificRowData(rowIndex, selectedShareClass);
  }

  private updateSpecificRowData(
    rowIndex: number,
    selectedShareClass: any
  ): void {
    this.rowData[rowIndex].selectedShareclass = selectedShareClass.link;
    this.rowData[rowIndex].docs = cloneDeep(
      this.stateData[rowIndex].literatureData[selectedShareClass.id]
    );
    this.rowData[rowIndex].viewMoreHref = this.getProductDetailLink(
      this.rowData[rowIndex].product,
      selectedShareClass.id as ShareClassCode
    );
    this.rowData = [...this.rowData];
    this.cdr.detectChanges();
  }
  ngOnDestroy() {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }
}
